import React, { useState } from 'react'

import { NavLink as RouterNavLink, NavLinkProps, useParams } from 'react-router-dom'
import { Add as AddIcon } from '@material-ui/icons'

import { useQueryPaginator } from 'src/components/Table'
import { CaseDialog } from 'src/modules/supply_chain/components/CaseDialog'
import { CaseListTable } from 'src/components/Table/list/Case'
import { DamagedListTable } from 'src/components/Table/list/Damaged'
import { BarcodedUnitListTable } from 'src/components/Table/list/BarcodedUnit'
import { FoundListTable } from 'src/components/Table/list/Found'
import { LostListTable } from 'src/components/Table/list/Lost'
import { ProductDetailTable } from 'src/components/Table/detail/Product'
import { ProductDialog } from 'src/modules/supply_chain/components/ProductDialog'
import { EventListTable } from 'src/components/Table/list/Event'
import { FreightOrderUnitListTable } from 'src/components/Table/list/FreightOrderUnit'
import { HierarchyDetail } from 'src/modules/supply_chain/components/HierarchyDetail'
import { ImageGallery } from 'src/components/ImageGallery'
import { ParcelOrderUnitListTable } from 'src/components/Table/list/ParcelOrderUnit'
import { SkuListTable } from 'src/components/Table/list/Sku'
import { UnitListTable } from 'src/components/Table/list/Unit'

import { skuit } from 'src/services/skuit'
import { useQuery } from 'react-query'

import { DetailPage } from 'src/components/Pages/DetailPage'

const NavLink = React.forwardRef((props: NavLinkProps, ref: React.Ref<HTMLAnchorElement>) => (
  <RouterNavLink innerRef={ref} {...props} />
))
NavLink.displayName = 'NavLink'

interface PathProps {
  guid: string
}

export const ProductPage = () => {
  const { guid } = useParams<PathProps>()
  const [createOpen, setCreateOpen] = useState(false)
  const [caseDialogOpen, setCaseDialogOpen] = useState(false)

  const product = useQuery(['product-details', guid], () =>
    skuit.sc.products.retrieve(guid)
  )
  const config = { params: { page_size: 5, products: guid } }
  const casesPaginator = useQueryPaginator('case-list', skuit.sc.cases.list, config)
  const skusPaginator = useQueryPaginator('sku-list', skuit.sc.skus.list, config)
  const unitsPaginator = useQueryPaginator('unit-list', skuit.sc.units.list, config)
  const damagedPaginator = useQueryPaginator('damaged-list', skuit.sc.damaged.list, config)
  const barcodedPaginator = useQueryPaginator(
    'barcoded-unit-list',
    skuit.sc.barcodedUnits.list,
    config
  )
  const lostPaginator = useQueryPaginator('lost-list', skuit.sc.lost.list, {
    params: { page_size: 5, products: guid },
  })
  const foundPaginator = useQueryPaginator('found-list', skuit.sc.found.list, {
    params: { page_size: 5, products: guid },
  })
  const orderUnitConfig = {
    params: { page_size: 5, products: guid, status: 'received,processing' },
  }
  const freightOrderUnitsPaginator = useQueryPaginator(
    'freight-order-unit-list',
    skuit.sc.freight.orders.units.list,
    orderUnitConfig
  )
  const parcelOrderUnitsPaginator = useQueryPaginator(
    'parcel-order-unit-list',
    skuit.sc.parcel.orders.units.list,
    orderUnitConfig
  )
  const eventsPaginator = useQueryPaginator('event-list', skuit.sc.events.list, {
    params: { page_size: 5, references: guid },
  })

  const refresh = () => {
    product.refetch()
    eventsPaginator.refresh()
  }

  const exportFilters = {
    guid: guid
  }
  const orderExportFilters = {
    status: 'received,processing'
  }

  const DownloadManagerMap: any = {
    case: skuit.sc.cases.export,
    sku: skuit.sc.skus.export,
    unit: skuit.sc.units.export,
    damage: skuit.sc.damaged.export,
    barcodedUnit: skuit.sc.barcodedUnits.export,
    lost: skuit.sc.lost.export,
    found: skuit.sc.found.export,
    freight: skuit.sc.freight.orders.export,
    parcel: skuit.sc.parcel.orders.export,
  }

  const FieldsMap: any = {
    case: [
      'guid',
      'num_units',
      'primary_barcode',
      'primary_barcode_type'
    ],
    sku: [
      'client_guid',
      'client_name',
      'guid',
      'is_active',
      'num_available',
      'num_awaiting_shipment',
      'num_damaged',
      'num_reserved',
      'num_units',
      'sku',
      'storage_health',
      'track_date_received',
    ],
    unit: [
      'client_guid',
      'client_name',
      'date_received',
      'facility_guid',
      'facility_name',
      'location_guid',
      'location_name',
      'name_path',
      'num_units',
      'product_guid',
      'product_name',
      'sku_guid',
      'sku_name',
    ],
    damage: [
      'asn_guid',
      'asn_tracking_number',
      'client_guid',
      'client_name',
      'damage_reason',
      'damaged_on',
      'facility',
      'facility_guid',
      'guid',
      'notes',
      'path',
      'product_guid',
      'product_name',
      'sku',
      'sku_guid',
    ],
    barcodedUnit: [
      'client_guid',
      'client_name',
      'guid',
      'path',
      'product_guid',
      'product_name',
    ],
    lost: [
      'client_guid',
      'client_name',
      'container_guid',
      'container_name',
      'date_received',
      'facility_guid',
      'facility_name',
      'guid',
      'num_units',
      'original_container_guid',
      'original_container_name',
      'original_place_guid',
      'original_place_name',
      'path',
      'product_guid',
      'product_name',
      'sku_guid',
      'sku_name',
    ],
    found: [
      'client_guid',
      'client_name',
      'container_guid',
      'container_name',
      'facility_guid',
      'facility_name',
      'guid',
      'num_units',
      'path',
      'place_guid',
      'place_name',
      'product_guid',
      'product_name',
      'sku_guid',
      'sku_name',
    ],
    freight: [
      'client_guid',
      'client_name',
      'container_guid',
      'container_name',
      'container_path',
      'container_type',
      'num_fulfilled',
      'num_picked',
      'num_units',
      'order_guid',
      'order_name',
      'sku_guid',
      'sku_name',
    ],
    parcel: [
      'client_guid',
      'client_name',
      'num_fulfilled',
      'num_picked',
      'num_units',
      'order_guid',
      'order_name',
      'sku_guid',
      'sku_name',
    ]
  }

  return (
    <>
      {createOpen && (
        <ProductDialog
          open={createOpen}
          handleClose={() => {
            setCreateOpen(false)
          }}
        />
      )}
      {caseDialogOpen && (
        <CaseDialog
          productGuid={guid}
          open={caseDialogOpen}
          handleClose={() => setCaseDialogOpen(false)}
          onSave={() => {
            casesPaginator.refresh()
          }}
        />
      )}
      <DetailPage
        obj={product}
        title="Product"
        refresh={refresh}
        breadcrumbs={[{ title: 'Dashboard', url: '/' }, { title: 'Products', url: '/inventory/products' }]}
        detailComponent={<ProductDetailTable obj={product} refresh={refresh} />}
        menuItems={[
          { Icon: AddIcon, actions: [{ title: 'New Product', onClick: () => setCreateOpen(true) }] },
        ]}
      >
        <ImageGallery
          title="Product Images"
          images={product.data?.images || []}
          loading={product.isLoading}
        />
        <CaseListTable
          paginator={casesPaginator}
          fields={['numUnits', 'primaryBarcode']}
          extraActions={[{
            title: '+ Case',
            onClick: () => {
              setCaseDialogOpen(true)
            },
          }]}
          exportConfig={{
            exportFields: FieldsMap.case,
            exportAPI: DownloadManagerMap.case,
            exportFilters: exportFilters,
          }}
        />
        <SkuListTable
          paginator={skusPaginator}
          fields={['sku', 'client', 'numAvailable', 'numReserved']}
          exportConfig={{
            exportFields: FieldsMap.sku,
            exportAPI: DownloadManagerMap.sku,
            exportFilters: exportFilters,
          }}
        />
        <UnitListTable
          paginator={unitsPaginator}
          fields={['path', 'client', 'sku', 'numUnits', 'dateReceived']}
          exportConfig={{
            exportFields: FieldsMap.unit,
            exportAPI: DownloadManagerMap.unit,
            exportFilters: exportFilters,
          }}
         />
        <DamagedListTable
          paginator={damagedPaginator}
          fields={['guid', 'client', 'sku', 'path']}
          exportConfig={{
            exportFields: FieldsMap.damage,
            exportAPI: DownloadManagerMap.damage,
            exportFilters: exportFilters,
          }}
        />
        <BarcodedUnitListTable
          paginator={barcodedPaginator}
          fields={['guid', 'client', 'product']}
          exportConfig={{
            exportFields: FieldsMap.barcodedUnit,
            exportAPI: DownloadManagerMap.barcodedUnit,
            exportFilters: exportFilters,
          }}
         />
        <HierarchyDetail title="Storage Map" roots={[]} />
        <LostListTable
          paginator={lostPaginator}
          fields={['sku', 'client', 'numUnits', 'dateReceived', 'path']}
          exportConfig={{
            exportFields: FieldsMap.lost,
            exportAPI: DownloadManagerMap.lost,
            exportFilters: exportFilters,
          }}
         />
        <FoundListTable
          paginator={foundPaginator}
          fields={['path', 'client', 'sku', 'numUnits']}
          exportConfig={{
            exportFields: FieldsMap.found,
            exportAPI: DownloadManagerMap.found,
            exportFilters: exportFilters,
          }}
         />
        <FreightOrderUnitListTable
          paginator={freightOrderUnitsPaginator}
          fields={['order', 'sku', 'numUnits', 'numPicked', 'numFulfilled']}
          exportConfig={{
            exportFields: FieldsMap.freight,
            exportAPI: DownloadManagerMap.freight,
            exportFilters: {...exportFilters, ...orderExportFilters},
          }}
        />
        <ParcelOrderUnitListTable
          paginator={parcelOrderUnitsPaginator}
          fields={['order', 'sku', 'numUnits', 'numPicked', 'numFulfilled']}
          exportConfig={{
            exportFields: FieldsMap.parcel,
            exportAPI: DownloadManagerMap.parcel,
            exportFilters: {...exportFilters, ...orderExportFilters},
          }}
        />
        <EventListTable
          paginator={eventsPaginator}
          title="Product Events"
          fields={['type', 'data', 'by', 'on']}
        />
      </DetailPage>
    </>
  )
}
